import React, { useEffect, useState, useContext } from 'react';
import { X, User, Lock, Loader2 } from 'lucide-react';
import UserAvatar from './UserAvatar';
import { authService } from '../../services/auth';
import { ToastContext } from '../../App';
import { formatBrazilianPhoneNumber } from '../../utils/fieldCache';
import { LoginModalContext } from '../../App';
// Add a helper function to manage profile data in localStorage
const profileStorage = {
  getProfileData: () => {
    const data = localStorage.getItem('profileData');
    return data ? JSON.parse(data) : null;
  },
  setProfileData: data => {
    localStorage.setItem('profileData', JSON.stringify({
      ...data,
      timestamp: Date.now()
    }));
  },
  isDataFresh: () => {
    const data = profileStorage.getProfileData();
    if (!data || !data.timestamp) return false;
    // Consider data fresh if less than 5 minutes old
    const fiveMinutesInMs = 5 * 60 * 1000;
    return Date.now() - data.timestamp < fiveMinutesInMs;
  }
};
const ProfileModal = ({
  isOpen,
  onClose,
  user,
  onLogout,
  activeTab = 'profile',
  setActiveTab
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    telefone: '',
    empresaTipo: '',
    cargo: '',
    funcionariosQuantidade: '',
    urlSite: '',
    comoConheceu: '',
    pretendeContratarSeo: '',
    area_de_atuacao: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const {
    addToast
  } = useContext(ToastContext);
  const {
    openLoginModal
  } = useContext(LoginModalContext);
  useEffect(() => {
    if (isOpen) {
      // Reset error and success messages when modal is opened
      setError('');
      setSuccess('');
      // Load cached profile data
      const cachedData = profileStorage.getProfileData();
      if (cachedData) {
        setProfileData({
          firstname: cachedData.firstname || '',
          lastname: cachedData.lastname || '',
          email: cachedData.email || '',
          telefone: cachedData.telefone ? formatBrazilianPhoneNumber(cachedData.telefone) : '',
          empresaTipo: cachedData.empresaTipo || '',
          cargo: cachedData.cargo || '',
          funcionariosQuantidade: cachedData.funcionariosQuantidade || '',
          urlSite: cachedData.urlSite || '',
          comoConheceu: cachedData.comoConheceu || '',
          pretendeContratarSeo: cachedData.pretendeContratarSeo || '',
          area_de_atuacao: cachedData.area_de_atuacao || ''
        });
        // If data is not fresh, refresh it in background
        if (!profileStorage.isDataFresh()) {
          loadUserProfile(false);
        }
      } else {
        // If no cached data at all, show loading state and load from API
        setIsLoadingProfile(true);
        loadUserProfile(true);
      }
    }
  }, [isOpen]);
  const loadUserProfile = async (showLoading = true) => {
    if (showLoading) {
      setIsLoadingProfile(true);
    }
    try {
      const userData = await authService.getProfile();
      // Format and set the profile data
      const formattedData = {
        firstname: userData.firstname || '',
        lastname: userData.lastname || '',
        email: userData.email || '',
        telefone: userData.telefone ? formatBrazilianPhoneNumber(userData.telefone) : '',
        empresaTipo: userData.empresaTipo || '',
        cargo: userData.cargo || '',
        funcionariosQuantidade: userData.funcionariosQuantidade || '',
        urlSite: userData.urlSite || '',
        comoConheceu: userData.comoConheceu || '',
        pretendeContratarSeo: userData.pretendeContratarSeo || '',
        area_de_atuacao: userData.area_de_atuacao || ''
      };
      setProfileData(formattedData);
      // Save to localStorage with timestamp
      profileStorage.setProfileData(userData);
    } catch (error) {
      console.error('Failed to load user profile:', error);
      if (showLoading) {
        setError('Falha ao carregar o perfil do usuário');
      }
    } finally {
      if (showLoading) {
        setIsLoadingProfile(false);
      }
    }
  };
  if (!isOpen) return null;
  const handlePhoneChange = e => {
    const formattedPhone = formatBrazilianPhoneNumber(e.target.value);
    setProfileData({
      ...profileData,
      telefone: formattedPhone
    });
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      // Ensure URL starts with http:// or https://
      let urlSite = profileData.urlSite.trim();
      if (urlSite && !urlSite.match(/^https?:\/\//)) {
        urlSite = 'https://' + urlSite;
      }
      const response = await authService.updateProfile({
        firstname: profileData.firstname,
        lastname: profileData.lastname,
        telefone: profileData.telefone.replace(/\D/g, ''),
        empresaTipo: profileData.empresaTipo,
        cargo: profileData.cargo,
        funcionariosQuantidade: profileData.funcionariosQuantidade,
        urlSite: urlSite,
        comoConheceu: profileData.comoConheceu,
        pretendeContratarSeo: profileData.pretendeContratarSeo,
        area_de_atuacao: profileData.area_de_atuacao
      });
      // Update the cached profile data
      if (response.data && response.data.user) {
        profileStorage.setProfileData(response.data.user);
      }
      // Close the modal
      onClose();
      // Show toast notification
      addToast('success', 'Perfil atualizado com sucesso!');
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.message).join(', ');
        setError(`Erro de validação: ${errorMessages}`);
      } else {
        setError(error.response?.data?.message || 'Erro ao atualizar perfil. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handlePasswordChange = async e => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    // Validação de senha completa
    if (passwordData.newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      setIsLoading(false);
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('As senhas não conferem');
      setIsLoading(false);
      return;
    }
    try {
      await authService.changePassword(passwordData.currentPassword, passwordData.newPassword);
      // Clear password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      // Close the profile modal
      onClose();
      // Logout the user
      onLogout();
      // Show toast notification
      addToast('success', 'Senha alterada com sucesso! Por favor, faça login novamente.');
      // Open login modal with relogin flag set to true
      setTimeout(() => {
        openLoginModal(true);
      }, 300);
    } catch (error) {
      setError(error.response?.data?.message || 'Erro ao alterar senha. Verifique a senha atual.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" data-id="element-96" data-mp="2idaw5wudtebo">
      <div className="relative w-full max-w-md p-6 mx-4 bg-white rounded-lg shadow-xl h-[650px] max-h-[90vh] flex flex-col overflow-hidden" data-id="element-97" data-mp="2ddojboigu03e">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700" aria-label="Close" disabled={isLoading} data-id="element-98" data-mp="bkqknc20cdaj">
          <X size={20} data-id="element-99" data-mp="3bkxjtskdabe0" />
        </button>
        <div className="flex items-center space-x-4 mb-6" data-id="element-100" data-mp="3bm7iojz1nnlp">
          <UserAvatar user={user} data-id="element-101" data-mp="3gks7zocrcmvd" />
          <div data-id="element-102" data-mp="3gk0k6zpejtfn">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-103" data-mp="3gmmrqzcam8xv">
              {profileData.firstname} {profileData.lastname}
            </h2>
            <p className="text-sm text-gray-600" data-id="element-104" data-mp="36u79s20zxkcz">
              {profileData.email || user.email}
            </p>
          </div>
        </div>
        {isLoadingProfile ? <div className="flex-grow flex justify-center items-center" data-id="element-105" data-mp="2xu124wrzbelq">
            <Loader2 size={24} className="animate-spin text-[#1649ff]" data-id="element-106" data-mp="2xvc7h112ouey" />
          </div> : <div className="flex-grow flex flex-col overflow-hidden" data-id="element-107" data-mp="2jw2ojtlhfm7a">
            <div className="flex border-b border-gray-200 mb-6" data-id="element-108" data-mp="2nwwcgmyxd106">
              <button className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'profile' ? 'border-[#1649ff] text-[#1649ff]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('profile')} disabled={isLoading} data-id="element-109" data-mp="i366vnx2hle7">
                <User size={16} className="mr-2" data-id="element-110" data-mp="22lqhl8j99qnp" />
                Perfil
              </button>
              <button className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'security' ? 'border-[#1649ff] text-[#1649ff]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('security')} disabled={isLoading} data-id="element-111" data-mp="2gclrmo5hqdsv">
                <Lock size={16} className="mr-2" data-id="element-112" data-mp="2lanap92x2d4c" />
                Segurança
              </button>
            </div>
            {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md" data-id="element-113" data-mp="2uc3lvj1k2j9m">
                {error}
              </div>}
            {success && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md" data-id="element-114" data-mp="14xp119tnrgje">
                {success}
              </div>}
            <div className="flex-grow overflow-y-auto px-1" data-id="element-115" data-mp="3clnraql2evxt">
              {activeTab === 'profile' ? <form onSubmit={handleSubmit} className="space-y-4 px-1" data-id="element-116" data-mp="37mkntakqgxry">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-117" data-mp="37op0agwp753u">
                    <div data-id="element-118" data-mp="33livldp8hlzh">
                      <label htmlFor="firstname" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-119" data-mp="33n3h6dtklul7">
                        Nome
                      </label>
                      <input type="text" id="firstname" value={profileData.firstname} onChange={e => setProfileData({
                  ...profileData,
                  firstname: e.target.value
                })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} data-id="element-120" data-mp="3ra1tr0ekkidu" />
                    </div>
                    <div data-id="element-121" data-mp="qgdi7wh4di74">
                      <label htmlFor="lastname" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-122" data-mp="vddskjl05741">
                        Sobrenome
                      </label>
                      <input type="text" id="lastname" value={profileData.lastname} onChange={e => setProfileData({
                  ...profileData,
                  lastname: e.target.value
                })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} data-id="element-123" data-mp="14d95asu7ha66" />
                    </div>
                  </div>
                  <div data-id="element-124" data-mp="3k5pmtaelmz54">
                    <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-125" data-mp="3k6h8ntie9ii7">
                      Email
                    </label>
                    <input type="email" id="email" name="email" value={profileData.email} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] bg-gray-50 shadow-sm text-gray-900" disabled autoComplete="username" data-id="element-126" data-mp="3t4jimyk3vlhm" />
                  </div>
                  <div data-id="element-127" data-mp="2migzrayy9i10">
                    <label htmlFor="telefone" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-128" data-mp="2mhnxonaxrgp7">
                      Telefone
                    </label>
                    <input type="tel" id="telefone" value={profileData.telefone} onChange={handlePhoneChange} placeholder="(11) 99999-9999" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} autoComplete="tel" data-id="element-129" data-mp="11zdewvtcrso1" />
                  </div>
                  <div data-id="element-130" data-mp="5a5sllkwtfj2">
                    <label htmlFor="urlSite" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-131" data-mp="5axeg4opfyw5">
                      URL do site
                    </label>
                    <input type="url" id="urlSite" name="url" value={profileData.urlSite} onChange={e => setProfileData({
                ...profileData,
                urlSite: e.target.value
              })} placeholder="https://www.example.com" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} autoComplete="url" data-id="element-132" data-mp="23oqm93dk8u24" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-133" data-mp="m8m6h7b8zopw">
                      A URL deve começar com http:// ou https://
                    </p>
                  </div>
                  <div data-id="element-134" data-mp="cazna8mx1b04">
                    <label htmlFor="empresaTipo" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-135" data-mp="cbhgsxi71b4c">
                      Segmento
                    </label>
                    <select id="empresaTipo" value={profileData.empresaTipo} onChange={e => setProfileData({
                ...profileData,
                empresaTipo: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} data-id="element-136" data-mp="2jwgahnv5jxfu">
                      <option value="" disabled data-id="element-137" data-mp="2a6513lsp8w1k">
                        Selecione o segmento
                      </option>
                      <option value="Agência / Consultoria" data-id="element-138" data-mp="2ez7mh6nboz6o">
                        Agência / Consultoria
                      </option>
                      <option value="Agronegócio" data-id="element-139" data-mp="1mhc3zm0cci5d">Agronegócio</option>
                      <option value="Autoconhecimento" data-id="element-140" data-mp="1mfivs2a2xpv9">Autoconhecimento</option>
                      <option value="Automotiva" data-id="element-141" data-mp="1efcubbn8jqeg">Automotiva</option>
                      <option value="Bens de Consumo / Alimentos" data-id="element-142" data-mp="1ag43aejky6ml">
                        Bens de Consumo / Alimentos
                      </option>
                      <option value="E-commerce" data-id="element-143" data-mp="1o8to4fndh5i4">E-commerce</option>
                      <option value="Educação" data-id="element-144" data-mp="1j8xlajqevk45">Educação</option>
                      <option value="Empresa pública / ONGs" data-id="element-145" data-mp="vhs3hz0wuhr7">
                        Empresa pública / ONGs
                      </option>
                      <option value="Energia" data-id="element-146" data-mp="rhrp2edta6k5">Energia</option>
                      <option value="Eventos" data-id="element-147" data-mp="15gr3yubiv734">Eventos</option>
                      <option value="Financeiro" data-id="element-148" data-mp="15e4z52g0n5ol">Financeiro</option>
                      <option value="Imobiliário" data-id="element-149" data-mp="10gd788fdfjke">Imobiliário</option>
                      <option value="Indústria" data-id="element-150" data-mp="2guv7os7ww9ld">Indústria</option>
                      <option value="Infoprodutos" data-id="element-151" data-mp="2gx7ov35uifxd">Infoprodutos</option>
                      <option value="Mídia comunicação" data-id="element-152" data-mp="2bvs133ca9vba">
                        Mídia comunicação
                      </option>
                      <option value="Mobilidade" data-id="element-153" data-mp="3tw9hp8h4ak7s">Mobilidade</option>
                      <option value="Moda / Vestuário" data-id="element-154" data-mp="2p6qwi56yfy3">Moda / Vestuário</option>
                      <option value="Religião / Espiritualidade" data-id="element-155" data-mp="3kua0af26xe4b">
                        Religião / Espiritualidade
                      </option>
                      <option value="Saúde / Beleza / Bem-Estar" data-id="element-156" data-mp="3pu64ofesv7ke">
                        Saúde / Beleza / Bem-Estar
                      </option>
                      <option value="Serviços gerais" data-id="element-157" data-mp="ld1y1j6asvi8">Serviços gerais</option>
                      <option value="Tecnologia / SaaS" data-id="element-158" data-mp="7n89z32maf9k">
                        Tecnologia / SaaS
                      </option>
                      <option value="Telecomunicações" data-id="element-159" data-mp="cki8d87zi19j">Telecomunicações</option>
                      <option value="Transporte / Logística" data-id="element-160" data-mp="10b435xsz9p1l">
                        Transporte / Logística
                      </option>
                      <option value="Turismo / Lazer" data-id="element-161" data-mp="153f679j0lqyd">Turismo / Lazer</option>
                      <option value="Varejo" data-id="element-162" data-mp="3160mbrazch3c">Varejo</option>
                      <option value="Outro" data-id="element-163" data-mp="2wesc4t1tsrp4">Outro</option>
                    </select>
                  </div>
                  <div data-id="element-164" data-mp="2rewah0glpccq">
                    <label htmlFor="area_de_atuacao" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-165" data-mp="2rg7d2wyb8e29">
                      Área de atuação
                    </label>
                    <select id="area_de_atuacao" value={profileData.area_de_atuacao} onChange={e => setProfileData({
                ...profileData,
                area_de_atuacao: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} data-id="element-166" data-mp="3k1qpviyk9ljc">
                      <option value="" disabled data-id="element-167" data-mp="a51dotv0lmgc">
                        Selecione a área
                      </option>
                      <option value="Administrativo" data-id="element-168" data-mp="j7srv1f9ywbo">Administrativo</option>
                      <option value="Comercial" data-id="element-169" data-mp="j5pqjl8o9grn">Comercial</option>
                      <option value="Contábil" data-id="element-170" data-mp="nzasfoddyuw4">Contábil</option>
                      <option value="Customer Success" data-id="element-171" data-mp="sxvrvlktdaji">Customer Success</option>
                      <option value="E-commerce" data-id="element-172" data-mp="szyf5xul0pkj">E-commerce</option>
                      <option value="Financeiro" data-id="element-173" data-mp="xv5giz0dpi6a">Financeiro</option>
                      <option value="Marketing" data-id="element-174" data-mp="11y24z6clo8i5">Marketing</option>
                      <option value="Operação" data-id="element-175" data-mp="16xeopi86czgi">Operação</option>
                      <option value="RH" data-id="element-176" data-mp="16wv8c5y6aj4l">RH</option>
                      <option value="SEO" data-id="element-177" data-mp="33px28gy40agu">SEO</option>
                      <option value="Tecnologia" data-id="element-178" data-mp="33rhsvvhaz99f">Tecnologia</option>
                      <option value="Outra área" data-id="element-179" data-mp="38pt4obr5rtua">Outra área</option>
                    </select>
                  </div>
                </form> : <form onSubmit={handlePasswordChange} className="space-y-4 px-1" data-id="element-180" data-mp="2xh4c4llpn0x8">
                  {/* Hidden email field to associate with the password change */}
                  <input type="email" name="email" value={profileData.email} autoComplete="username" className="hidden" readOnly data-id="element-181" data-mp="377fsj7mkz2l0" />
                  <div data-id="element-182" data-mp="3g9fbi5h5ogql">
                    <label htmlFor="currentPassword" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-183" data-mp="3l65oul1ksy8b">
                      Senha atual
                    </label>
                    <input type="password" id="currentPassword" name="current-password" value={passwordData.currentPassword} onChange={e => setPasswordData({
                ...passwordData,
                currentPassword: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} required autoComplete="current-password" data-id="element-184" data-mp="1qu8scdoa126e" />
                  </div>
                  <div data-id="element-185" data-mp="mtrasog8k5a6">
                    <label htmlFor="newPassword" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-186" data-mp="mt97x8z2ista">
                      Nova senha
                    </label>
                    <input type="password" id="newPassword" name="new-password" value={passwordData.newPassword} onChange={e => setPasswordData({
                ...passwordData,
                newPassword: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} required minLength={6} autoComplete="new-password" data-id="element-187" data-mp="32lyc0xrz8noj" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-188" data-mp="2cdsgjny892sc">
                      Mínimo de 6 caracteres
                    </p>
                  </div>
                  <div data-id="element-189" data-mp="22di6gc7hobhe">
                    <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-190" data-mp="22ea2fo500b87">
                      Confirmar nova senha
                    </label>
                    <input type="password" id="confirmPassword" name="confirm-password" value={passwordData.confirmPassword} onChange={e => setPasswordData({
                ...passwordData,
                confirmPassword: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} required minLength={6} autoComplete="new-password" data-id="element-191" data-mp="1irzw6ppc4lej" />
                  </div>
                </form>}
            </div>
            <div className="mt-6 pt-4" data-id="element-192" data-mp="2lgo3iezc84zz">
              {activeTab === 'profile' ? <button onClick={handleSubmit} className="w-full py-2 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center" disabled={isLoading} data-id="element-193" data-mp="irmuu88l7d7l">
                  {isLoading ? <>
                      <Loader2 size={16} className="mr-2 animate-spin" data-id="element-194" data-mp="3vzpvtnl9kwjj" />
                      Salvando...
                    </> : 'Salvar alterações'}
                </button> : <button onClick={handlePasswordChange} className="w-full py-2 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center" disabled={isLoading} data-id="element-195" data-mp="dt39a8xwo3fr">
                  {isLoading ? <>
                      <Loader2 size={16} className="mr-2 animate-spin" data-id="element-196" data-mp="39s3t75521t3o" />
                      Alterando...
                    </> : 'Alterar senha'}
                </button>}
            </div>
          </div>}
      </div>
    </div>;
};
export default ProfileModal;